import React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Select, MenuItem, FormControl, Tooltip, Box, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';


// Unified Overall Standings table that supports grouping by competition/event/category/division
export default function OverallStandings(props) {
  const {
    adjustedRows = [],
    teamModes = {},
    setTeamModes,
    setAutoAppliedModes,
    affiliatedMode,
    affiliatedTeamUids = [],
    setManualOverrides,
    groupedTopResults = {},
    eventsScores = {},
    categoriesMap = {},
    divisionsMap = {}
  } = props;

  // Local UI state for OverallStandings (decoupled from global report selector)
  const [groupBy, setGroupBy] = React.useState('competition');
  const [topN, setTopN] = React.useState(0);
  const [includeTies] = React.useState(true);
  // Local filters for "By Event" grouped view (labels)
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [divisionFilter, setDivisionFilter] = React.useState('');

  const showAdjusted = (affiliatedMode && affiliatedMode !== 'none') || Object.values(teamModes || {}).some(m => m !== 'normal');
  const defaultSortField = showAdjusted ? 'place' : 'overallPlace';

  // helper to print HTML using hidden iframe (avoids popup blockers)
  const printHtml = (html) => {
    try {
      const id = 'print-iframe';
      let iframe = document.getElementById(id);
      if (iframe) iframe.parentNode.removeChild(iframe);
      iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.id = id;
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      iframe.onload = function () {
        try {
          iframe.contentWindow.focus();
          // Use setTimeout to ensure rendering
          setTimeout(() => {
            iframe.contentWindow.print();
            // remove iframe after printing
            setTimeout(() => {
              if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
            }, 500);
          }, 300);
        } catch (err) {
          console.error('Iframe print error', err);
        }
      };
    } catch (err) {
      console.error('printHtml failed', err);
      alert('Printing failed: ' + (err && err.message));
    }
  };

  // Build base columns (common fields)
  const baseColumns = [
    { field: 'teamNumber', headerName: 'Team Number', width: 120 },
    { field: 'teamName', headerName: 'Team Name', width: 240 },
    { field: 'category', headerName: 'Category', width: 160 },
    { field: 'division', headerName: 'Division', width: 160 },
    {
      field: 'affiliated', headerName: 'Affiliated', width: 120, renderCell: (params) => {
        const uid = params.row?.uid || params.id;
        const isAffThis = Boolean(params.value);
        const names = params.row?.affiliatedEventNames || [];
        const isAffAny = affiliatedTeamUids.includes(uid);

        let title = 'Not affiliated';
        if (isAffThis && names.length > 0) {
          title = `Affiliated with this event${names.length > 1 ? '. Also affiliated with: ' + names.join(', ') : ''}`;
        } else if (isAffAny && names.length > 0) {
          title = `Affiliated with other events: ${names.join(', ')}`;
        } else if (isAffAny) {
          title = 'Affiliated with at least one event';
        }

        return (
          <Tooltip title={title}>
            <span>{isAffThis ? <CheckCircleIcon sx={{ color: 'green' }} /> : (isAffAny ? <LinkIcon sx={{ color: 'orange' }} /> : <CancelIcon sx={{ color: 'red' }} />)}</span>
          </Tooltip>
        );
      }
    }
  ];

  // Build competition-specific columns (totals and overall places)
  const competitionColumns = React.useMemo(() => {
    const cols = [...baseColumns];
    cols.push({ field: 'originalTotal', headerName: 'Original Total', width: 140 });
    cols.push({ field: 'overallPlace', headerName: 'Original Place', width: 120 });
    if (showAdjusted) {
      cols.push({ field: 'adjustedTotal', headerName: 'Adjusted Total', width: 140 });
      cols.push({ field: 'place', headerName: 'Adjusted Place', width: 120 });
    }
    return cols;
  }, [baseColumns, showAdjusted]);

  // Mode selector column
  const modeColumn = {
    field: 'mode', headerName: 'Mode', width: 320, renderCell: (params) => {
      const uid = params.row?.uid || params.id;
      const value = teamModes[uid] || 'normal';
      const isAffiliatedAny = affiliatedTeamUids.includes(uid) || Boolean(params.row?.affiliated) || ((params.row?.affiliatedEventNames || []).length > 0);
      const infoText = `Adjusted - Affiliated Average: If a team is affiliated with an event, that corresponding score will be set to the average.\nAdjusted - Affiliated Max: If a team is affiliated with an event, that corresponding score will be set to the worst in that event.\nAdjusted - Affiliated Up Next: If a team is affiliated with an event, that score is set to the next non-affiliated team's score.\nAdjusted - Affiliated Exclude: If a team is affiliated with an event, that corresponding score will be set to 1500.`;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl variant="standard" sx={{ minWidth: 220 }}>
            <Select
              value={value}
              onChange={(e) => {
                const v = e.target.value;
                if (setManualOverrides) setManualOverrides(prev => ({ ...(prev || {}), [uid]: true }));
                if (setAutoAppliedModes) setAutoAppliedModes(prev => { const copy = { ...(prev || {}) }; delete copy[uid]; return copy; });
                if (setTeamModes) setTeamModes(prev => ({ ...prev, [uid]: v }));
              }}
              size="small"
            >
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="total_average">Total Average</MenuItem>
              <MenuItem value="affiliated_average" disabled={!isAffiliatedAny}>Affiliated Average (if below avg)</MenuItem>
              <MenuItem value="affiliated_max" disabled={!isAffiliatedAny}>Affiliated Max (worst score)</MenuItem>
              <MenuItem value="affiliated_up_next" disabled={!isAffiliatedAny}>Affiliated Up Next (next non-affiliated score)</MenuItem>
              <MenuItem value="exclude" disabled={!isAffiliatedAny}>Affiliated Exclude (set to 1500)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    }
  };

  // Build combined normalized rows depending on groupBy
  const buildCombinedRows = React.useCallback(() => {
    const adjustedMap = (adjustedRows || []).reduce((m, r) => { m[r.id || r.uid] = r; return m; }, {});

    if (groupBy === 'competition') {
      // Competition rows already conform to columns
      return (groupedTopResults && Array.isArray(groupedTopResults.competition) && groupedTopResults.competition.length > 0)
        ? groupedTopResults.competition.map(r => ({ ...r, id: r.id || r.uid }))
        : adjustedRows.map(r => ({ ...r, id: r.id || r.uid }));
    }

    if (groupBy === 'event') {
      const flattened = Object.keys(groupedTopResults.event || {}).flatMap(evUid => {
        const rows = (groupedTopResults.event || {})[evUid] || [];
        const ev = eventsScores?.[evUid] || {};
        const num = ev?.event?.number;
        const name = ev?.event?.name;
        const label = num ? `#${num} ${name || ''}`.trim() : (name || evUid);
        return rows.map((r, idx) => ({
          id: `${evUid}-${r.teamUid}-${idx}`,
          uid: r.teamUid,
          eventUid: evUid,
          eventNumber: num || null,
          eventName: name || '',
          teamNumber: r.teamNumber || '',
          teamName: r.teamName || '',
          category: (categoriesMap && categoriesMap[r.category]) ? categoriesMap[r.category] : (r.category || ''),
          division: (divisionsMap && divisionsMap[r.division]) ? divisionsMap[r.division] : (r.division || ''),
          affiliated: r.affiliated || false,
          affiliatedEventNames: r.affiliatedEventNames || [],
          originalTotal: r.finalScoreRaw,
          adjustedTotal: r.finalScoreAssociated ?? r.finalScoreRaw,
          overallPlace: r.place,
          // per-context places: original event place, and associated/adjusted event place
          eventPlace: r.place,
          adjustedPlace: (r.placeAssociated !== undefined && r.placeAssociated !== null) ? r.placeAssociated : '',
          mode: (adjustedMap[r.teamUid] && adjustedMap[r.teamUid].mode) || 'normal',
          groupLabel: label
        }));
      });
      return flattened;
    }

    if (groupBy === 'category') {
      const flattened = Object.keys(groupedTopResults.category || {}).flatMap(cat => {
        const rows = (groupedTopResults.category || {})[cat] || [];
        const label = (categoriesMap && categoriesMap[cat]) ? categoriesMap[cat] : cat;
        return rows.map((r, idx) => ({
          id: r.id || r.uid || `cat-${cat}-${idx}`,
          uid: r.uid || r.id,
          teamNumber: r.teamNumber || '',
          teamName: r.teamName || '',
          category: label,
          division: (divisionsMap && divisionsMap[r.division]) ? divisionsMap[r.division] : (r.division || ''),
          affiliated: r.affiliated || false,
          affiliatedEventNames: r.affiliatedEventNames || [],
          originalTotal: r.originalTotal,
          adjustedTotal: r.adjustedTotal,
          overallPlace: r.overallPlace,
          place: r.place,
          // per-context places
          eventPlace: '',
          categoryPlace: r.categoryPlace || '',
          adjustedPlace: (r.categoryPlaceAssociated !== undefined && r.categoryPlaceAssociated !== null) ? r.categoryPlaceAssociated : (r.categoryPlace || ''),
          divisionPlace: '',
          mode: r.mode || 'normal',
          groupLabel: label
        }));
      });
      return flattened;
    }

    if (groupBy === 'division') {
      const flattened = Object.keys(groupedTopResults.division || {}).flatMap(divn => {
        const rows = (groupedTopResults.division || {})[divn] || [];
        const label = (divisionsMap && divisionsMap[divn]) ? divisionsMap[divn] : divn;
        return rows.map((r, idx) => ({
          id: r.id || r.uid || `div-${divn}-${idx}`,
          uid: r.uid || r.id,
          teamNumber: r.teamNumber || '',
          teamName: r.teamName || '',
          category: (categoriesMap && categoriesMap[r.category]) ? categoriesMap[r.category] : (r.category || ''),
          division: label,
          affiliated: r.affiliated || false,
          affiliatedEventNames: r.affiliatedEventNames || [],
          originalTotal: r.originalTotal,
          adjustedTotal: r.adjustedTotal,
          overallPlace: r.overallPlace,
          place: r.place,
          // per-context places
          eventPlace: '',
          categoryPlace: '',
          divisionPlace: r.divisionPlace || '',
          adjustedPlace: (r.divisionPlaceAssociated !== undefined && r.divisionPlaceAssociated !== null) ? r.divisionPlaceAssociated : (r.divisionPlace || ''),
          mode: r.mode || 'normal',
          groupLabel: label
        }));
      });
      return flattened;
    }

    return [];
  }, [adjustedRows, groupedTopResults, groupBy, eventsScores, categoriesMap, divisionsMap]);

  const combinedRows = buildCombinedRows();

  // Apply Top-N filtering locally so the UI control behaves immediately
  const getDisplayedRows = React.useCallback(() => {
    if (!topN || topN <= 0) {
      if (groupBy === 'competition') return combinedRows;
      // otherwise continue so grouped views can apply filters and show all matching rows
    }
    const placeFieldCompetition = showAdjusted ? 'place' : 'overallPlace';

    if (groupBy === 'competition') {
      const rowsWithPlace = combinedRows.filter(r => Number.isFinite(r[placeFieldCompetition]));
      rowsWithPlace.sort((a,b) => a[placeFieldCompetition] - b[placeFieldCompetition]);
      if (rowsWithPlace.length === 0) return [];
      const nthPlace = rowsWithPlace[Math.min(topN - 1, rowsWithPlace.length - 1)][placeFieldCompetition];
      if (includeTies) return rowsWithPlace.filter(r => r[placeFieldCompetition] <= nthPlace);
      return rowsWithPlace.slice(0, topN);
    }

    // For grouped views, apply Top-N per groupLabel using the context place
    const contextPlaceField = groupBy === 'event' ? 'eventPlace' : (groupBy === 'category' ? 'categoryPlace' : 'divisionPlace');
    const grouped = {};
    combinedRows.forEach(r => {
      const key = r.groupLabel || '(unknown)';
      grouped[key] = grouped[key] || [];
      grouped[key].push(r);
    });

    const out = [];
    Object.keys(grouped).forEach(k => {
      let rows = grouped[k];

      // For Event grouping we need to compute two rankings: original and adjusted
      if (groupBy === 'event') {
        if (categoryFilter) rows = rows.filter(r => r.category === categoryFilter);
        if (divisionFilter) rows = rows.filter(r => r.division === divisionFilter);

        // Original ranking
        const scoredOriginal = rows.filter(r => Number.isFinite(r.originalTotal)).slice();
        scoredOriginal.sort((a,b) => (Number(a.originalTotal)||0) - (Number(b.originalTotal)||0));
        let lastScoreO = null; let lastPlaceO = 0;
        for (let i=0;i<scoredOriginal.length;i++){
          const s = Number(scoredOriginal[i].originalTotal)||0;
          if (i===0) { lastPlaceO = 1; scoredOriginal[i].eventPlaceOriginal = 1; lastScoreO = s; }
          else { if (s===lastScoreO) scoredOriginal[i].eventPlaceOriginal = lastPlaceO; else { scoredOriginal[i].eventPlaceOriginal = i+1; lastPlaceO = i+1; lastScoreO = s; } }
        }

        // Adjusted ranking
        const scoredAdjusted = rows.filter(r => Number.isFinite(r.adjustedTotal)).slice();
        scoredAdjusted.sort((a,b) => (Number(a.adjustedTotal)||0) - (Number(b.adjustedTotal)||0));
        let lastScoreA = null; let lastPlaceA = 0;
        for (let i=0;i<scoredAdjusted.length;i++){
          const s = Number(scoredAdjusted[i].adjustedTotal)||0;
          if (i===0) { lastPlaceA = 1; scoredAdjusted[i].eventPlaceAdjusted = 1; lastScoreA = s; }
          else { if (s===lastScoreA) scoredAdjusted[i].eventPlaceAdjusted = lastPlaceA; else { scoredAdjusted[i].eventPlaceAdjusted = i+1; lastPlaceA = i+1; lastScoreA = s; } }
        }

        // Attach computed places back to rows by uid
        const mapOrig = {}; scoredOriginal.forEach(r=>{ if (r.uid) mapOrig[r.uid]=r.eventPlaceOriginal; });
        const mapAdj = {}; scoredAdjusted.forEach(r=>{ if (r.uid) mapAdj[r.uid]=r.eventPlaceAdjusted; });
        rows.forEach(r => {
          r.eventPlace = mapOrig[r.uid] ?? (Number.isFinite(r.eventPlace) ? r.eventPlace : '');
          r.adjustedPlace = mapAdj[r.uid] ?? (r.adjustedPlace !== undefined ? r.adjustedPlace : '');
        });

        // choose list for Top-N
        const useList = showAdjusted ? scoredAdjusted : scoredOriginal;
        if (useList.length === 0) return; // skip
        if (!topN || topN <= 0) { out.push(...useList); }
        else if (useList.length <= topN) { out.push(...useList); }
        else {
          const nthPlace = showAdjusted ? useList[Math.min(topN-1, useList.length-1)].eventPlaceAdjusted : useList[Math.min(topN-1, useList.length-1)].eventPlaceOriginal;
          if (includeTies) {
            if (showAdjusted) out.push(...useList.filter(r => (r.eventPlaceAdjusted || 9999) <= nthPlace));
            else out.push(...useList.filter(r => (r.eventPlaceOriginal || 9999) <= nthPlace));
          } else {
            out.push(...useList.slice(0, topN));
          }
        }

      } else {
        // category/division grouped views: compute original and adjusted contextual places similarly
        // contextPlaceField for original (e.g., categoryPlace or divisionPlace)
        const scoreOrigField = 'originalTotal';
        const scoreAdjField = 'adjustedTotal';

        const scoredOrig = rows.filter(r => Number.isFinite(r[scoreOrigField])).slice();
        scoredOrig.sort((a,b) => (Number(a[scoreOrigField])||0)-(Number(b[scoreOrigField])||0));
        let lastS = null; let lastP = 0;
        for (let i=0;i<scoredOrig.length;i++){
          const s = Number(scoredOrig[i][scoreOrigField])||0;
          if (i===0) { lastP=1; scoredOrig[i].contextPlaceOriginal = 1; lastS=s; }
          else { if (s===lastS) scoredOrig[i].contextPlaceOriginal = lastP; else { scoredOrig[i].contextPlaceOriginal = i+1; lastP=i+1; lastS=s; } }
        }

        const scoredAdj = rows.filter(r => Number.isFinite(r[scoreAdjField])).slice();
        scoredAdj.sort((a,b) => (Number(a[scoreAdjField])||0)-(Number(b[scoreAdjField])||0));
        lastS = null; lastP = 0;
        for (let i=0;i<scoredAdj.length;i++){
          const s = Number(scoredAdj[i][scoreAdjField])||0;
          if (i===0) { lastP=1; scoredAdj[i].contextPlaceAdjusted = 1; lastS=s; }
          else { if (s===lastS) scoredAdj[i].contextPlaceAdjusted = lastP; else { scoredAdj[i].contextPlaceAdjusted = i+1; lastP=i+1; lastS=s; } }
        }

        const mapOrigC = {}; scoredOrig.forEach(r=>{ if (r.uid) mapOrigC[r.uid]=r.contextPlaceOriginal; });
        const mapAdjC = {}; scoredAdj.forEach(r=>{ if (r.uid) mapAdjC[r.uid]=r.contextPlaceAdjusted; });

        rows.forEach(r=>{
          if (groupBy==='category') {
            r.categoryPlace = mapOrigC[r.uid] ?? (r.categoryPlace || '');
            r.adjustedPlace = mapAdjC[r.uid] ?? (r.adjustedPlace !== undefined ? r.adjustedPlace : '');
          } else {
            r.divisionPlace = mapOrigC[r.uid] ?? (r.divisionPlace || '');
            r.adjustedPlace = mapAdjC[r.uid] ?? (r.adjustedPlace !== undefined ? r.adjustedPlace : '');
          }
        });

        // select list
        const useList = showAdjusted ? scoredAdj : scoredOrig;
        if (useList.length === 0) return;
        if (!topN || topN<=0) out.push(...useList);
        else if (useList.length<=topN) out.push(...useList);
        else {
          const nth = useList[Math.min(topN-1, useList.length-1)][ showAdjusted ? 'contextPlaceAdjusted' : 'contextPlaceOriginal' ];
          if (includeTies) out.push(...useList.filter(r => (r[ showAdjusted ? 'contextPlaceAdjusted' : 'contextPlaceOriginal']||9999) <= nth));
          else out.push(...useList.slice(0, topN));
        }
      }
    });

    return out;
  }, [combinedRows, topN, includeTies, groupBy, showAdjusted, categoryFilter, divisionFilter]);

  const displayedRows = getDisplayedRows();

  // Build unified columns: include groupLabel only for non-competition
  const groupLabelHeader = groupBy === 'event' ? 'Event' : (groupBy === 'category' ? 'Category' : 'Division');

  // Top-level printable builder used by both toolbars
  const buildPrintableHtmlForRows = (rowsInput) => {
    const rows = Array.isArray(rowsInput) ? rowsInput : [];

    if (!rows || rows.length === 0) return '';

    // Helper to build header / table for a set of rows
    const buildTableHtml = (title, rowsForTable, scoreField, placeField) => {
      // detect tied scores (same numeric score appearing more than once)
      const scoreCounts = {};
      rowsForTable.forEach(r => {
        const v = Number.isFinite(Number(r[scoreField])) ? Number(r[scoreField]) : null;
        if (v !== null) {
          const k = String(v);
          scoreCounts[k] = (scoreCounts[k] || 0) + 1;
        }
      });
      const tieSet = new Set(Object.keys(scoreCounts).filter(k => scoreCounts[k] > 1));

      const tieNote = tieSet.size > 0 ? `<div style="margin-bottom:8px;font-size:13px;color:#b35f00;"><strong>Note:</strong> Highlighted rows indicate tied scores.</div>` : '';

      const rowsHtml = rowsForTable.map(r => {
        const affiliatedVal = r.affiliated ? 'Yes' : 'No';
        const scoreVal = Number.isFinite(Number(r[scoreField])) ? Number(r[scoreField]) : '';
        const place = Number.isFinite(r[placeField]) ? r[placeField] : '';
        const scoreKey = Number.isFinite(Number(r[scoreField])) ? String(Number(r[scoreField])) : null;
        const isTie = scoreKey !== null && tieSet.has(scoreKey);
        const trStyle = isTie ? 'background-color: #fff8c4;' : '';
        const tieMarker = isTie ? '<span style="color:#b35f00; font-weight:700; margin-left:6px;">TIED</span>' : '';
        return `<tr style="${trStyle}"><td>${r.teamNumber || ''}</td><td>${r.teamName || ''}${tieMarker}</td><td>${r.category || ''}</td><td>${r.division || ''}</td><td>${affiliatedVal}</td><td>${scoreVal}</td><td>${place}</td></tr>`;
      }).join('');

      return `\n<div style="page-break-after: always;"><h2>${title}</h2>${tieNote}<table border="1" cellspacing="0" cellpadding="5"><thead><tr><th>Team Number</th><th>Team Name</th><th>Category</th><th>Division</th><th>Affiliated</th><th>Score</th><th>Place</th></tr></thead><tbody>${rowsHtml}</tbody></table></div>`;
    };

    // Build explanation block at top if any adjustment modes are present in the rows
    const modesPresent = new Set(rows.map(r => r.mode).filter(Boolean));
    const explanations = [];
    if (modesPresent.has('affiliated_average') || modesPresent.has('total_average')) explanations.push('Adjusted - Affiliated Average: If a team is affiliated with an event, that corresponding score will be set to the average.');
    if (modesPresent.has('affiliated_max')) explanations.push('Adjusted - Affiliated Max: If a team is affiliated with an event, that corresponding score will be set to the worst in that event.');
    if (modesPresent.has('exclude')) explanations.push('Adjusted - Affiliated Exclude: If a team is affiliated with an event, that corresponding score will be set to 1500.');
    const explanationsHtml = explanations.length ? `<div style="margin-bottom:12px; font-size:13px;"><strong>Adjustment Notes:</strong><ul>${explanations.map(e=>`<li>${e}</li>`).join('')}</ul></div>` : '';

    // For competition-wide print: if any adjusted exists use adjusted ranking, otherwise original
    const competitionHasAdjusted = rows.some(r => Number.isFinite(r.adjustedTotal) && r.adjustedTotal !== r.originalTotal);
    if (groupBy === 'competition') {
      if (competitionHasAdjusted) {
        // compute adjusted places
        const arr = rows.filter(r => Number.isFinite(r.adjustedTotal)).slice().sort((a,b)=>Number(a.adjustedTotal)-Number(b.adjustedTotal));
        let last = null; let p = 0;
        arr.forEach((r,i)=>{ const s=Number(r.adjustedTotal)||0; if (i===0){p=1; r._printPlace=p; last=s;} else { if (s===last){ r._printPlace=p } else { r._printPlace=i+1; p=i+1; last=s } } });
        return explanationsHtml + buildTableHtml('Overall Standings (Adjusted)', arr, 'adjustedTotal', '_printPlace');
      }
      // original
      const arr = rows.filter(r => Number.isFinite(r.originalTotal)).slice().sort((a,b)=>Number(a.originalTotal)-Number(b.originalTotal));
      let last = null; let p = 0;
      arr.forEach((r,i)=>{ const s=Number(r.originalTotal)||0; if (i===0){p=1; r._printPlace=p; last=s;} else { if (s===last){ r._printPlace=p } else { r._printPlace=i+1; p=i+1; last=s } } });
      return explanationsHtml + buildTableHtml('Overall Standings', arr, 'originalTotal', '_printPlace');
    }

    // Grouped views: build per-group using adjusted ordering if group has adjusted scores
    const grouped = rows.reduce((acc, row) => { const k = row.groupLabel || '(unknown)'; if (!acc[k]) acc[k]={ label:k, rows:[] }; acc[k].rows.push(row); return acc }, {});
    const orderedKeys = Object.keys(grouped).sort((a,b)=>{ const A=grouped[a], B=grouped[b]; const aNum=(A.rows&&A.rows[0]&&Number(A.rows[0].eventNumber))||null; const bNum=(B.rows&&B.rows[0]&&Number(B.rows[0].eventNumber))||null; if(aNum&&bNum) return aNum-bNum; return a.localeCompare(b)});

    return explanationsHtml + orderedKeys.map(k=>{
      const item = grouped[k];
      const groupRows = item.rows.slice();
      const groupHasAdjusted = groupRows.some(r => Number.isFinite(r.adjustedTotal) && r.adjustedTotal !== r.originalTotal);
      if (groupHasAdjusted) {
        // compute adjusted ranking
        const arr = groupRows.filter(r=>Number.isFinite(r.adjustedTotal)).slice().sort((a,b)=>Number(a.adjustedTotal)-Number(b.adjustedTotal));
        let last = null; let p=0;
        arr.forEach((r,i)=>{ const s=Number(r.adjustedTotal)||0; if(i===0){p=1; r._printPlace=p; last=s;} else { if(s===last){ r._printPlace=p } else { r._printPlace=i+1; p=i+1; last=s } } });
        return buildTableHtml(`${k} (Adjusted)`, arr, 'adjustedTotal', '_printPlace');
      } else {
        // compute original ranking
        const arr = groupRows.filter(r=>Number.isFinite(r.originalTotal)).slice().sort((a,b)=>Number(a.originalTotal)-Number(b.originalTotal));
        let last=null; let p=0;
        arr.forEach((r,i)=>{ const s=Number(r.originalTotal)||0; if(i===0){p=1; r._printPlace=p; last=s;} else { if(s===last){ r._printPlace=p } else { r._printPlace=i+1; p=i+1; last=s } } });
        return buildTableHtml(k, arr, 'originalTotal', '_printPlace');
      }
    }).join('');
  };

  const handleCustomPrintUnified = () => {
    console.log('Custom print (unified) clicked');
    try {
      const rows = Array.isArray(displayedRows) ? displayedRows : [];
      if (rows.length === 0) { alert('No rows to print for the current selection/filters.'); return; }
      const content = buildPrintableHtmlForRows(rows);
      const html = `<!doctype html><html><head><title>Overall Standings</title></head><body>${content}<script>window.onload=function(){setTimeout(()=>{window.print();},200);};</script></body></html>`;
      printHtml(html);
    } catch (err) {
      console.error('Print failed', err);
      alert('Failed to prepare print: ' + (err && err.message));
    }
  };

  const combinedHasAdjusted = React.useMemo(() => {
    // Show adjusted columns only if global adjustments are active or any row has been adjusted
    const anyRowAdjusted = (combinedRows || []).some(r => {
      if (r && typeof r.mode === 'string' && r.mode !== 'normal') return true;
      if (Number.isFinite(r.adjustedTotal) && Number.isFinite(r.originalTotal) && r.adjustedTotal !== r.originalTotal) return true;
      return false;
    });
    return showAdjusted || anyRowAdjusted;
  }, [showAdjusted, combinedRows]);

  // Build unified columns for grouped views
  if (groupBy !== 'competition') {
    let finalCols;
    if (groupBy === 'event') {
      const affiliatedCol = baseColumns.find(c => c.field === 'affiliated') || { field: 'affiliatedFlag', headerName: 'Affiliated', width: 100, valueGetter: (params) => (params.row?.affiliated ? 'Yes' : 'No') };
      finalCols = [
        { field: 'eventNumber', headerName: 'Event Number', width: 110 },
        { field: 'eventName', headerName: 'Event Name', width: 220 },
        { field: 'teamNumber', headerName: 'Team Number', width: 120 },
        { field: 'teamName', headerName: 'Team Name', width: 240 },
        { field: 'category', headerName: 'Category', width: 160 },
        { field: 'division', headerName: 'Division', width: 160 },
        affiliatedCol,
        { field: 'originalTotal', headerName: 'Original Score', width: 140 },
        { field: 'eventPlace', headerName: 'Event Place', width: 120 },
        { field: 'adjustedTotal', headerName: 'Adjusted Score', width: 140 },
        { field: 'adjustedPlace', headerName: 'Adjusted Place', width: 120 },
        modeColumn
      ];
    } else {
      const cols = [{ field: 'groupLabel', headerName: groupLabelHeader, width: 220 }, ...baseColumns];
      cols.push({ field: 'originalTotal', headerName: 'Original Score', width: 140 });

      if (groupBy === 'category') {
        cols.push({ field: 'categoryPlace', headerName: 'Category Place', width: 120 });
      } else if (groupBy === 'division') {
        cols.push({ field: 'divisionPlace', headerName: 'Division Place', width: 120 });
      }
      if (combinedHasAdjusted) cols.push({ field: 'adjustedTotal', headerName: 'Adjusted Score', width: 140 });
      if (combinedHasAdjusted) cols.push({ field: 'adjustedPlace', headerName: 'Adjusted Place', width: 120 });

      cols.push(modeColumn);
      finalCols = cols;
    }
    const sortModel = groupBy === 'event'
      ? [{ field: 'eventNumber', sort: 'asc' }, { field: 'eventPlace', sort: 'asc' }]
      : [{ field: 'groupLabel', sort: 'asc' }, { field: (showAdjusted ? 'place' : 'overallPlace'), sort: 'asc' }];

    return (
      <div style={{ width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
          <FormControl variant="standard" sx={{ minWidth: 180 }}>
            <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} size="small">
              <MenuItem value="competition">Competition</MenuItem>
              <MenuItem value="event">By Event</MenuItem>
              <MenuItem value="category">By Category</MenuItem>
              <MenuItem value="division">By Division</MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="standard" sx={{ minWidth: 140 }}>
            <Select value={topN} onChange={(e) => { const v = e.target.value; if (v === -1) { const custom = parseInt(window.prompt('Enter custom Top N', '10') || '0', 10); setTopN(Number.isFinite(custom) ? custom : 0); } else setTopN(Number(v)); }} size="small">
              <MenuItem value={0}>All</MenuItem>
              <MenuItem value={3}>Top 3</MenuItem>
              <MenuItem value={6}>Top 6</MenuItem>
              <MenuItem value={10}>Top 10</MenuItem>
              <MenuItem value={-1}>Custom...</MenuItem>
            </Select>
          </FormControl>

          {groupBy === 'event' && (
            <>
              <FormControl variant="standard" sx={{ minWidth: 180 }}>
                <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} displayEmpty size="small">
                  <MenuItem value=''>All Categories</MenuItem>
                  {Object.keys(categoriesMap || {}).map(k => (<MenuItem key={k} value={categoriesMap[k]}>{categoriesMap[k]}</MenuItem>))}
                </Select>
              </FormControl>

              <FormControl variant="standard" sx={{ minWidth: 160 }}>
                <Select value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)} displayEmpty size="small">
                  <MenuItem value=''>All Divisions</MenuItem>
                  {Object.keys(divisionsMap || {}).map(k => (<MenuItem key={k} value={divisionsMap[k]}>{divisionsMap[k]}</MenuItem>))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>

        <div style={{ height: 600, width: '100%' }}>
          <DataGrid key={showAdjusted ? 'adjusted' : 'original'} rows={displayedRows} columns={finalCols} slots={{ toolbar: GridToolbar }} pageSizeOptions={[25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel } }} />
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="outlined" onClick={() => { console.log('Bottom Custom Print clicked'); handleCustomPrintUnified(); }}>Custom Print</Button>
        </Box>
      </div>
    );
  }

  // Default competition rendering path
  const sortModel = (groupBy === 'competition') ? [{ field: defaultSortField, sort: 'asc' }] : [{ field: 'groupLabel', sort: 'asc' }, { field: defaultSortField, sort: 'asc' }];

  return (
    <div style={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
        <FormControl variant="standard" sx={{ minWidth: 180 }}>
          <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} size="small">
            <MenuItem value="competition">Competition</MenuItem>
            <MenuItem value="event">By Event</MenuItem>
            <MenuItem value="category">By Category</MenuItem>
            <MenuItem value="division">By Division</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="standard" sx={{ minWidth: 140 }}>
          <Select value={topN} onChange={(e) => { const v = e.target.value; if (v === -1) { const custom = parseInt(window.prompt('Enter custom Top N', '10') || '0', 10); setTopN(Number.isFinite(custom) ? custom : 0); } else setTopN(Number(v)); }} size="small">
            <MenuItem value={0}>All</MenuItem>
            <MenuItem value={3}>Top 3</MenuItem>
            <MenuItem value={5}>Top 5</MenuItem>
            <MenuItem value={10}>Top 10</MenuItem>
            <MenuItem value={-1}>Custom...</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid key={defaultSortField} rows={displayedRows} columns={[...competitionColumns, modeColumn]} slots={{ toolbar: GridToolbar }} pageSizeOptions={[25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel } }} />
      </div>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="outlined" onClick={() => { console.log('Competition bottom Custom Print clicked'); handleCustomPrintUnified(); }}>Custom Print</Button>
      </Box>
    </div>
  );
}
